import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  ref,
  unref,
  watch,
} from 'vue'

import
  FloatingActionButton
from '@/components/buttons/FloatingActionButton.vue';

import {
  AddCardsDialog,
  SearchFilter,
} from '@/components/cards/children';

import {
  Card,
  CardManagerType,
  deleteCards,
  fetchCards,
  loadCardStates,
  SearchFilterParameters,
  updateOrCreateCardList,
} from '@/components/cards/scripts';

import {
  cardActions,
  isStringAndHasLength,
  ProfipayCardStates,
} from '@/components/cards/scripts';

import {useI18n} from '@/composables/useI18n';

import {
  httpMethod,
} from "@/scripts/Common/api";

import {
  CardStates,
} from "@/Enums";

import {
  useSnackbar,
} from "@/composables/useSnackbar";

import InfoSnackbar from "@/components/children/InfoSnackbar.vue";

import {
  useDataTableDefaults,
} from "@/composables/useDataTableDefaults";

import {
  OperationResults,
} from "@/scripts/Common";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  editCardsPolicy,
} from "@/authorization/policies/administrateCardsPolicy";

import {
  DeleteItemsDialog,
} from "@/components/dialogs";

import {
  isCardActive
} from "@/components/children/cardStatusIcon/scripts/helper";

import
  CardStatusIcon
from "@/components/children/cardStatusIcon/CardStatusIcon.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'AdministerCards',
  emits: [
  'onFetchError',
  'onItemError'
],
  setup(__props, { emit: emits }) {

const {
  t,
} = useI18n();

const {
  dataTableOptions,
  dataTableFooter,
} = useDataTableDefaults();

const availableCards = ref<Card[] | undefined>();
const profipayCardStates = ref<ProfipayCardStates[]>([]);
const isAddDialogVisible = ref<boolean>(false);
const isDeleteDialogVisible = ref<boolean>(false)
const itemsToUpdate = ref<Card[]>([]);
const selectedCards = ref<Card[]>([]);
const snackbarRef = ref(null);
const {
  snackbarStatus,
  snackbarText,
  showSnackbarResponse
} = useSnackbar(snackbarRef);

let queryParams = {
  cardState: undefined,
  cardNumber: undefined,
  managerTypes: undefined,
  managerName: undefined
} as SearchFilterParameters;



const headers = computed(() => [
  {
    text: t('card_number'),
    value: 'cardNumber'
  },
  {
    text: t('card_reference'),
    value: 'reference',
    sortable: false
  },
  {
    text: t('card_state'),
    value: 'state',
    sortable: false
  },
  {
    text: t('card_manager_type'),
    value: 'management.type',
    sortable: false
  },
  {
    text: t('card_manager_name'),
    value: 'management.name',
    sortable: false,
  },
  {
    text: t('actions'),
    value: 'edit',
    sortable: false
  }
]);

const translateCardState = (state: CardStates) => {
  switch (state) {
    case CardStates.Assigned: {
      return t('card_state_assigned');
    }
    case CardStates.Unassigned: {
      return t('card_state_unassigned');
    }
    default: {
      return t('card_state_unknown');
    }
  }
}

const translateCardManagerType = (card: Card) : string => {
  if(!card.management) {
    return "";
  }

  switch (card.management.type) {
    case CardManagerType.Customer:
      return t('card_manager_type_customer');
    case CardManagerType.Issuer:
      return t('card_manager_type_issuer');
    case CardManagerType.Operator:
      return t('card_manager_type_operator');
    default:
      return "";
  }
}

const onHandleFetchError = ({error}: { error: any }) => {
  emits('onFetchError', {error})
};

const onHandleItemError = ({response}: { response: OperationResults<any> }) => {
  emits('onItemError', {response})
};

const cardActionMap = (operationType: string) => {
  const items = {
    [cardActions.createCards]: t('action_create'),
    [cardActions.updateCards]: t('action_update'),
    [cardActions.deleteCards]: t('action_delete'),
  };

  return items[operationType as cardActions];
}

const setSnackbarStatus = ({response}: { response: Record<any, any> }) => showSnackbarResponse(response, cardActionMap);

const selectedCardNumbers = computed(
  () => unref(selectedCards)?.map((card => card.cardNumber))
);

const loadProfipayCardStates = async (cardNumbers: number[]) => {

  // we will send a request with the card numbers as a filter
  // therefore we want to return the first page with the same amount of items as the card numbers that we send
  const {
    error,
    response,
  } = await loadCardStates(cardNumbers, { page: 1, itemsPerPage: cardNumbers.length });

  if (error) {
    onHandleFetchError({error});
    return;
  }

  profipayCardStates.value = response;
}

const fetchAvailableCards = async () => {
  selectedCards.value = [];
  // set to empty array to clear any input
  availableCards.value = [];

  const {
    error,
    response,
  } = await fetchCards(dataTableOptions, queryParams);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  if (response && response.length > 0) {
    await loadProfipayCardStates((response as Card[]).map(element => element.cardNumber));
  }

  availableCards.value = response;
  itemsToUpdate.value = [];
};

const updateOrCreateCards = async (method: httpMethod, listOfCards: Card[]) => {
  const {
    error,
    response,
  } = await updateOrCreateCardList(method, listOfCards);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  setSnackbarStatus({response});

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  fetchAvailableCards();
};

const removeCards = async (listOfCardIds: number[]) => {
  const {
    error,
    response,
  } = await deleteCards(listOfCardIds);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  setSnackbarStatus({response});

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  // if we remove the same amount of cards we display in the table, we must decrement pagination
  if (listOfCardIds.length === unref(selectedCards).length) {
    dataTableOptions.value.pagination.page = Math.min(Math.max((unref(dataTableOptions.value.pagination.page) - 1), 1)
      , unref(dataTableOptions.value.pagination.pageCount));
  }

  fetchAvailableCards();
};

const onHandleItemDeleteClick = (item: Card) => {
  selectedCards.value = [item];
  isDeleteDialogVisible.value = true;
}

const onHandleDeleteBatch = () => {
  isDeleteDialogVisible.value = true;
};

const onHandleAddCards = () => {
  isAddDialogVisible.value = true;
};

const onHandleSaveBatch = () => {
  updateOrCreateCards(httpMethod.PUT, unref(itemsToUpdate));
};

const onHandleDialogAddConfirm = async (values: { cardStart: number, cardEnd: number, reference: string }) => {
  isAddDialogVisible.value = false;

  const {
    cardEnd,
    cardStart,
    reference,
  } = values;

  const counter = cardEnd - cardStart + 1;

  const cardsToCreate = [...Array(counter)].map((count: number, index: number) => {
    return {
      cardNumber: cardStart + index,
      id: 0,
      reference: counter === 1 ? reference : '',
    }
  });
  updateOrCreateCards(httpMethod.POST, cardsToCreate as Card[]);
};

const onHandleDialogAddCancel = () => {
  isAddDialogVisible.value = false;
};

const onHandleDialogDeleteConfirm = async () => {
  isDeleteDialogVisible.value = false;
  const cardIdsToDelete = selectedCards.value.map((selectedCard) => selectedCard.id);
  removeCards(cardIdsToDelete);
};

const onHandleDialogDeleteCancel = () => {
  isDeleteDialogVisible.value = false;
};

const setCardToUpdate = ({item, inputValue}: { item: Card, inputValue: string }) => {
  const updatedCard = {
    ...item,
    reference: inputValue,
  };

  const listOfCardsToUpdate = itemsToUpdate.value.filter((values) => values.id !== item.id);
  itemsToUpdate.value = [...listOfCardsToUpdate, updatedCard];
}

const onHandleSearchFilter = (searchFilter: SearchFilterParameters) => {
  queryParams = {...searchFilter};
  // if we are not on the first page, we want to reset the pagination
  if (dataTableOptions.value.pagination.page !== 1) {
    dataTableOptions.value.pagination.page = 1;
    return; // watcher will fetch the available cards
  }
  fetchAvailableCards();
}

const hasAnyCardUpdates = computed(() => itemsToUpdate.value.length > 0);

watch(() => [
  dataTableOptions.value.pagination.page,
  dataTableOptions.value.pagination.itemsPerPage,
  dataTableOptions.value.sorting,
], () => {
  fetchAvailableCards();
}, {deep: true});

onMounted(async () => {
  await fetchAvailableCards();
});

return { __sfc: true,t, dataTableOptions, dataTableFooter, availableCards, profipayCardStates, isAddDialogVisible, isDeleteDialogVisible, itemsToUpdate, selectedCards, snackbarRef, snackbarStatus, snackbarText, showSnackbarResponse, queryParams, emits, headers, translateCardState, translateCardManagerType, onHandleFetchError, onHandleItemError, cardActionMap, setSnackbarStatus, selectedCardNumbers, loadProfipayCardStates, fetchAvailableCards, updateOrCreateCards, removeCards, onHandleItemDeleteClick, onHandleDeleteBatch, onHandleAddCards, onHandleSaveBatch, onHandleDialogAddConfirm, onHandleDialogAddCancel, onHandleDialogDeleteConfirm, onHandleDialogDeleteCancel, setCardToUpdate, onHandleSearchFilter, hasAnyCardUpdates, FloatingActionButton, AddCardsDialog, SearchFilter, isStringAndHasLength, InfoSnackbar, hasAccessRights, editCardsPolicy, DeleteItemsDialog, isCardActive, CardStatusIcon }
}

})